/**
 * Slovan homepage hero image carousel with crossfade, line indicators,
 * and per-slide heading / CTA link sync.
 */
class SlovanHeroCarousel extends HTMLElement {
  /** @type {number} */
  #currentIndex = 0;

  /** @type {ReturnType<typeof setInterval> | null} */
  #timer = null;

  connectedCallback() {
    /** @type {HTMLElement[]} */
    this.slides = [...this.querySelectorAll('.slovan-hero-carousel__slide')];

    if (this.slides.length <= 1) return;

    this.duration = parseInt(this.dataset.duration, 10) || 5000;

    const heroElement = this.closest('.hero');
    const heroId = heroElement?.id;

    this.headingEl = heroElement?.querySelector('[data-slovan-hero-heading]') ?? null;
    this.ctaEl = heroElement?.querySelector('.slovan-hero__content .button') ?? null;

    /** @type {HTMLElement[]} */
    this.indicators = heroId
      ? [...document.querySelectorAll(`[data-carousel="${heroId}"] .slovan-hero-indicators__item`)]
      : [];

    this.indicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => this.goTo(index));
    });

    this.addEventListener('mouseenter', () => this.#stopAutoplay());
    this.addEventListener('mouseleave', () => this.#startAutoplay());

    this.#syncIndicators(this.#currentIndex);
    this.#syncSlideContent(this.#currentIndex);
    this.#startAutoplay();
  }

  disconnectedCallback() {
    this.#stopAutoplay();
  }

  /**
   * @param {number} index
   */
  goTo(index) {
    const count = this.slides.length;
    if (count === 0) return;

    index = ((index % count) + count) % count;

    this.slides[this.#currentIndex]?.classList.remove('is-active');
    this.#currentIndex = index;
    this.slides[this.#currentIndex]?.classList.add('is-active');

    this.#syncIndicators(this.#currentIndex);
    this.#syncSlideContent(this.#currentIndex);
    this.#startAutoplay();
  }

  /**
   * @param {number} index
   */
  #syncIndicators(index) {
    this.indicators.forEach((indicator, indicatorIndex) => {
      const isActive = indicatorIndex === index;
      const line = indicator.querySelector('.slovan-hero-indicators__line');

      indicator.setAttribute('aria-selected', isActive ? 'true' : 'false');
      line?.classList.remove('is-progressing');

      if (isActive) {
        if (line) void line.offsetWidth;
        line?.classList.add('is-progressing');
      }
    });
  }

  /**
   * @param {number} index
   */
  #syncSlideContent(index) {
    const slide = this.slides[index];
    if (!slide) return;

    const heading = slide.dataset.slideHeading;
    const link = slide.dataset.slideLink;

    if (this.headingEl && heading) {
      this.headingEl.textContent = heading;
    }

    if (this.ctaEl && link) {
      this.ctaEl.setAttribute('href', link);
    }
  }

  #next() {
    this.goTo(this.#currentIndex + 1);
  }

  #startAutoplay() {
    this.#stopAutoplay();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#timer = setInterval(() => this.#next(), this.duration);
  }

  #stopAutoplay() {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = null;
  }
}

if (!customElements.get('slovan-hero-carousel')) {
  customElements.define('slovan-hero-carousel', SlovanHeroCarousel);
}
